import { createStore } from "solid-js/store";

import type { Rarity } from "./stickerStore";

const BASE = `${window.location.origin}/stickers/plates`;

export type Plate = { id: string; name: string; url: string };
export type OwnedPlate = Plate & { src?: string; at?: number };
export type PlateItem = Plate & {
  price: string | number | null;
  rarity?: Rarity;
  owners?: number;
};
export type PlateCollection = { owned: OwnedPlate[]; equipped: string | null };

const [equipped, setEquipped] = createStore<Record<string, Plate | null>>({});
const asked = new Set<string>();
let queue: string[] = [];
let timer: number | undefined;

async function flush() {
  timer = undefined;
  const ids = queue.splice(0, 100);
  if (!ids.length) return;
  try {
    const r = await fetch(`${BASE}/list?ids=${ids.join(",")}`);
    const data = r.ok ? await r.json() : {};
    for (const id of ids) setEquipped(id, data[id] ?? null);
  } catch {
    for (const id of ids) asked.delete(id);
  }
  if (queue.length) timer = window.setTimeout(flush, 30);
}

/**
 * Placa equipada do usuario (reativo). Busca em lote na primeira leitura.
 */
export function plateOf(userId?: string): Plate | undefined {
  if (!userId) return undefined;
  if (!asked.has(userId)) {
    asked.add(userId);
    queue.push(userId);
    if (timer === undefined) timer = window.setTimeout(flush, 30);
  }
  return equipped[userId] ?? undefined;
}

/**
 * Catalogo da loja de placas
 */
export async function loadPlateCatalog(): Promise<PlateItem[]> {
  try {
    const r = await fetch(`${BASE}/catalog?t=${Date.now()}`, {
      cache: "no-store",
    });
    if (!r.ok) return [];
    const d = await r.json();
    return d.plates ?? [];
  } catch {
    return [];
  }
}

/**
 * Placas que o usuario possui e a equipada
 */
export async function fetchPlates(userId: string): Promise<PlateCollection> {
  const r = await fetch(`${BASE}/owned/${userId}`, { cache: "no-store" });
  if (!r.ok) throw new Error("erro ao carregar as placas");
  const d = await r.json();
  return { owned: d.owned ?? [], equipped: d.equipped ?? null };
}

/**
 * Equipa (id) ou remove (null) a placa do proprio usuario
 */
export async function savePlate(
  token: string,
  userId: string,
  id: string | null,
) {
  const r = await fetch(`${BASE}/equipped`, {
    method: "PUT",
    headers: { "x-session-token": token, "content-type": "application/json" },
    body: JSON.stringify({ id }),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error ?? "erro ao salvar a placa");
  asked.add(userId);
  setEquipped(userId, data.equipped ?? null);
  return data;
}
